import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { logout } from '../services/apiService';
import './Header.css';

const Header = ({ user, onShowScores, onReturnToMain, onShowUserConfig, onShowAdmin }) => {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuBtnRef = useRef(null);

  // Effect to handle clicks outside the dropdown and mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if click is outside dropdown and user button
      if (dropdownOpen && 
          dropdownRef.current && 
          userButtonRef.current && 
          !dropdownRef.current.contains(event.target) && 
          !userButtonRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }

      // Close mobile menu if click is outside mobile menu and mobile menu button
      if (mobileMenuOpen && 
          mobileMenuRef.current && 
          mobileMenuBtnRef.current && 
          !mobileMenuRef.current.contains(event.target) && 
          !mobileMenuBtnRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, mobileMenuOpen]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    await logout();
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="header">
      <div className="container">
        <div className="logo-container">
          <div className="logo" onClick={onReturnToMain}>
            <img src="/images/logo.png" alt="OverGuessr" />
          </div>
        </div>

        <nav className="main-nav">
          <ul className="nav-links">
            <li>
              <button 
                className="nav-link" 
                onClick={onReturnToMain}
              >
                <i className="fas fa-home"></i> {t('nav.home')}
              </button>
            </li>
            <li>
              <button 
                className="nav-link" 
                onClick={onShowScores}
              >
                <i className="fas fa-trophy"></i> {t('nav.scores')}
              </button>
            </li>
            {user ? (
              <li className="user-info">
                <button className="user-button" onClick={toggleDropdown} ref={userButtonRef}>
                  <span className="user-name">
                    <i className="fas fa-user"></i> {user.streamer_mode ? t('userConfig.streamerMode') : (user.battlenet_tag || t('scores.anonymousUser'))}
                  </span>
                  <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
                </button>
                {dropdownOpen && (
                  <div className="user-dropdown" ref={dropdownRef}>
                    <button className="dropdown-item" onClick={() => { setDropdownOpen(false); onShowUserConfig(); }}>
                      <i className="fas fa-cog"></i> {t('userConfig.settings')}
                    </button>
                    {user.is_admin && (
                      <button className="dropdown-item" onClick={() => { setDropdownOpen(false); onShowAdmin(); }}>
                        <i className="fas fa-tools"></i> {t('admin.title', 'Admin Panel')}
                      </button>
                    )}
                    <button className="dropdown-item" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt"></i> {t('auth.logout')}
                    </button>
                  </div>
                )}
              </li>
            ) : (
              <li className="login-btn">
                <a href="/auth/battlenet" className="btn btn-bnet">
                  <span className="bnet-icon">B</span>
                  <span>{t('auth.loginButton')}</span>
                </a>
              </li>
            )}
          </ul>
        </nav>

        <div className="mobile-menu">
          <div className="mobile-menu-container">
            <button className="mobile-menu-btn" onClick={toggleMobileMenu} ref={mobileMenuBtnRef}>
              <i className="fas fa-bars"></i>
            </button>
          </div>
          <ul className={`mobile-nav-links ${mobileMenuOpen ? '' : 'hidden'}`} ref={mobileMenuRef}>
            <li>
              <button 
                className="nav-link" 
                onClick={onReturnToMain}
              >
                <i className="fas fa-home"></i> {t('nav.home')}
              </button>
            </li>
            <li>
              <button 
                className="nav-link" 
                onClick={onShowScores}
              >
                <i className="fas fa-trophy"></i> {t('nav.scores')}
              </button>
            </li>
            <li className="language-selector">
              <button 
                className={`lang-btn ${i18n.language === 'es' ? 'active' : ''}`} 
                onClick={() => changeLanguage('es')}
              >
                ES
              </button>
            </li>
            {user ? (
              <li className="mobile-user-info">
                <div className="mobile-user-name">
                  <i className="fas fa-user"></i> {user.streamer_mode ? t('userConfig.streamerMode') : (user.username || user.battlenet_tag || t('scores.anonymousUser'))}
                </div>
                <button className="mobile-user-button" onClick={() => { setMobileMenuOpen(false); onShowUserConfig(); }}>
                  <i className="fas fa-cog"></i> {t('userConfig.settings')}
                </button>
                {user.is_admin && (
                  <button className="mobile-user-button" onClick={() => { setMobileMenuOpen(false); onShowAdmin(); }}>
                    <i className="fas fa-tools"></i> {t('admin.title', 'Admin Panel')}
                  </button>
                )}
                <button className="mobile-user-button" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i> {t('auth.logout')}
                </button>
              </li>
            ) : (
              <li id="mobile-login">
                <a href="/auth/battlenet" className="btn btn-bnet">
                  <span className="bnet-icon">B</span>
                  <span>{t('auth.loginButton')}</span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
